import PrintDocument from "../bvcomponents/popups/printPopup/printDocument";

import store from './../store';
import * as types from "./actionTypes";
import {convertImgToBase64URL, THUMBNAIL_SIZES} from "../misc/utils";
import {getFabricatorLogo} from "../misc/fabricatorHardcode";
import {featureThumbnailUrl} from "../misc/helpers";
import {WIZARD_GROUP_TYPES} from "../interfaces/wizardItemInterface";
import {baseNewImagesPath} from "../bvcomponents/assets";
import {ASYNC_SCRIPTS} from "../loadingLogic";
import {loadScriptPromise} from "../misc/loadScript";
import {registerFonts} from "../bvcomponents/popups/printPopup/printDocumentStyles";
import {IS_AS_WORK_ORDER} from "../config";

let printLogo = "";
let notFoundImage = "";

export const loadReceiptScript = async () => {
  await loadScriptPromise(ASYNC_SCRIPTS.bundleReactPdfRenderer, "bundleReactPdfRenderer");
  registerFonts();
}

export const getCurrentReceipt = async (assignQuotePopupNewCustomer, newEmployeeSelected, onRender, isContractSign) => {

  const {userState, serverState, fabricatorState, cartState, quotesState, priceWorksheetState,
    filterState, assignQuoteState, wizardState, sqftSlabState, printState, paymentState} = store.getState();

  const {assignQuotePopupCustomer, assignQuotePopupEmployee} = assignQuoteState;

  await loadReceiptScript();

  printLogo = IS_AS_WORK_ORDER || fabricatorState.receiptSettings?.unbrandedReceipt
    ? null
    : printLogo || await convertImgToBase64URL(getFabricatorLogo(fabricatorState));
  let cartItemsThumbnails = {};
  if (fabricatorState.receiptSettings && fabricatorState.receiptSettings.showThumbnailsAndDescriptions) {
    cartItemsThumbnails = await getCartItemThumbnails(cartState.cartItems, wizardState);
  }

  return window.ReactPdfRenderer.pdf(PrintDocument({
    fabricatorData: fabricatorState,
    userInfo: userState.userInfo,
    quotes: quotesState.quotes,
    cartItems: cartState.cartItems,
    priceWorksheetState,
    assignQuotePopupCustomer: assignQuotePopupNewCustomer || assignQuotePopupCustomer,
    employeeSelected: newEmployeeSelected || assignQuotePopupEmployee,
    locale: serverState.language,
    filterGroups: filterState.filterGroups,
    activeTheme: serverState.activeTheme,
    jobId: serverState.jobId,
    printLogo,
    wizardState,
    sqftSlabState,
    cartItemsThumbnails,
    printState,
    paymentState,
    onRender,
    isContractSign,
  }))
}

// export const getCurrentReceiptPages = async (withReceiptUrl) => {
//   try {
//     const pdfReceipt = await getCurrentReceipt();
//     if (pdfReceipt && pdfReceipt.container && pdfReceipt.container.render) {
//       await pdfReceipt.container.render();
//       if (pdfReceipt.container.document && pdfReceipt.container.document.getLayoutData) {
//         const layoutData = pdfReceipt.container.document.getLayoutData();
//         if (layoutData && Array.isArray(layoutData.children) && layoutData.children.length) {
//           if (withReceiptUrl) {
//             const receipt = await pdfReceipt.toBlob();
//             const receiptUrl = URL.createObjectURL(receipt);
//             return {receiptUrl, receiptPages: layoutData.children.length};
//           }
//           return layoutData.children.length
//         }
//       }
//     }
//     if (withReceiptUrl) {
//       return {receiptUrl: "", receiptPages: 0};
//     }
//     return 0
//   } catch (e) {
//     console.log("error rendering pdf receipt", e);
//     if (withReceiptUrl) {
//       return {receiptUrl: "", receiptPages: 0};
//     }
//     return 0
//   }
// }

export const getCurrentReceiptPages = async (withReceiptUrl) => {
  try {
    let imagesPromise = new Promise(async (resolve) => {
      const onRender = (onRenObj) => {
        // console.log("onRender!!", onRenObj);
        const receiptLayout = onRenObj ? onRenObj._INTERNAL__LAYOUT__DATA_ : null;
        const receiptPages = receiptLayout && receiptLayout.children ? receiptLayout.children.length : 1;
        const receiptUrl = URL.createObjectURL(onRenObj.blob);

        resolve({receiptUrl, receiptLayout, receiptPages})
      };
      const pdfReceipt2 = await getCurrentReceipt(null, null, onRender, true)
      await pdfReceipt2.toBlob();
    })
    if (withReceiptUrl) {
      return imagesPromise.then(response => response)
    }
    return 0
  } catch (e) {
    console.log("error rendering pdf receipt", e);
    if (withReceiptUrl) {
      return {receiptUrl: "", receiptPages: 0};
    }
    return 0
  }
}

// Thumbnails converted to data URLs, keyed by source URL. A receipt is rendered up to three times per
// save (retail receipt, cost receipt, then again once the job id is known) and a fabricator with
// showThumbnailsAndDescriptions has one image per cart item, so without this every render re-downloaded
// and re-encoded every thumbnail. The value is the pending promise so concurrent requests for the same
// URL share one load; failures are not kept, so a transient error is retried on the next render.
const thumbnailDataUrlCache = new Map();

const getCachedBase64Image = (url) => {
  if (!thumbnailDataUrlCache.has(url)) {
    const pending = convertImgToBase64URL(url).then((dataUrl) => {
      if (!dataUrl) {
        thumbnailDataUrlCache.delete(url);
      }
      return dataUrl;
    });
    thumbnailDataUrlCache.set(url, pending);
  }
  return thumbnailDataUrlCache.get(url);
};

const getNotFoundImage = async () => {
  if (!notFoundImage) {
    notFoundImage = await convertImgToBase64URL(`${baseNewImagesPath}images/image-not-found.png`);
  }
  return notFoundImage;
};

const getCartItemThumbnail = async (cartItem) => {
  try {
    const thumbnailUrl = featureThumbnailUrl(cartItem.cartFeature, THUMBNAIL_SIZES.small);
    return await getCachedBase64Image(thumbnailUrl) || await getNotFoundImage();
  } catch (e) {
    return `${baseNewImagesPath}images/image-not-found.png`;
  }
};

// Custom wizard images live on hosts without CORS headers, hence the proxy chain; the order matters and
// each step is only tried when the previous one produced nothing.
const getCustomWizardImage = async (customImageUrl) => {
  try {
    const candidates = [
      `https://corsproxy.io/?url=${encodeURIComponent(customImageUrl)}`,
      `https://proxy.corsfix.com/?${customImageUrl}`,
      customImageUrl,
      customImageUrl + "?not-from-cache-please",
    ];
    for (const candidate of candidates) {
      const dataUrl = await getCachedBase64Image(candidate);
      if (dataUrl) {
        return dataUrl;
      }
    }
    return await getNotFoundImage();
  } catch (e) {
    return `${baseNewImagesPath}images/image-not-found.png`;
  }
};

export const getCartItemThumbnails = async (cartItems, wizardState) => {
  const cartItemsThumbnails = {};
  const customWizardGroups = [];
  for (const wizard of wizardState.allWizardsValues) {
    for (const key of Object.keys(wizard.wizardSelectedValues)) {
      const wizardGroup = wizard.wizardSelectedValues[key].wizardGroup;
      if (wizardGroup && (wizardGroup.nodeType === WIZARD_GROUP_TYPES.custom) && wizardGroup.customImageUrl) {
        customWizardGroups.push(wizardGroup);
      }
    }
  }
  // all images load in parallel; the browser queues them per host, so this is bounded without our help
  await Promise.all([
    ...cartItems.map(async (cartItem) => {
      cartItemsThumbnails[cartItem.id] = await getCartItemThumbnail(cartItem);
    }),
    ...customWizardGroups.map(async (wizardGroup) => {
      cartItemsThumbnails[wizardGroup.id] = await getCustomWizardImage(wizardGroup.customImageUrl);
    }),
  ]);
  return cartItemsThumbnails
}

export const getOfflineReceiptImages = async () => {
  const {fabricatorState} = store.getState();
  notFoundImage = await convertImgToBase64URL(`${baseNewImagesPath}images/image-not-found.png`)
  printLogo = await convertImgToBase64URL(getFabricatorLogo(fabricatorState));
  await getCurrentReceiptPages(true);
  return true
}

export const printReceipt = async () => {
  const pdfReceipt = await getCurrentReceipt();
  const receipt = await pdfReceipt.toBlob();
  const fileURL = URL.createObjectURL(receipt);
  window.open(fileURL, "_blank");
}

export const setReceiptUrl = (receiptUrl) => ({
  type: types.SET_RECEIPT_URL,
  payload: {receiptUrl}
})

export const setReceiptSignature = (receiptSignature) => ({
  type: types.SET_RECEIPT_SIGNATURE,
  payload: {receiptSignature}
})

export const setReceiptReferral = (receiptReferral) => ({
  type: types.SET_RECEIPT_REFERRAL,
  payload: {receiptReferral}
})

// fields - a partial object with any of: receiptWorkOrderNumber, receiptHDLeadNumber, receiptBathroomLocation,
// receiptEstimateStart, receiptEstimatedText (home depot receipt only, filled in on the /sign page)
export const setReceiptHomeDepotFields = (fields) => ({
  type: types.SET_RECEIPT_HOME_DEPOT_FIELDS,
  payload: fields
})
